/**
 * Temporary simplified quoting tool for 3rd-party quoters.
 *
 * To remove this module:
 *   1. Delete src/modules/sp-quote/
 *   2. Remove the SpQuote import + /sp-quote route from App.js
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { calculateQuoteBands, money, formatMinutes, floorRateFor } from './pricing';
import { DEFAULT_SALES_TAX_RATE } from '../../utils/orderSalesTax';
import {
  RETAIL_BASELINES,
  EMBROIDERY_STITCH_TIERS,
  embroideryDigitizingFee,
} from './retailPricing';
import {
  buildSpQuoteLogFingerprint,
  logSpQuoteUsage,
  shouldLogSpQuote,
} from './logSpQuoteUsage';
import { buildPriceStops, stopIndexForPosition, positionForStopIndex, PRICE_STEP } from './priceSlider';

const SETTLED_QUOTE_LOG_MS = 20000;

const DECORATION_TYPES = [
  { value: 'screen', label: 'Screen Print' },
  { value: 'dtf', label: 'DTF' },
  { value: 'embroidery', label: 'Embroidery' },
];

const LOCATIONS = [
  { key: 'front', label: 'Front' },
  { key: 'back', label: 'Back' },
  { key: 'other', label: 'Other' },
];

const Page = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 32px 20px 24px;
  font-family: ${props => props.theme.fonts.primary};
  color: ${props => props.theme.colors.text};
  box-sizing: border-box;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 14px;
  display: flex;
  flex-direction: column;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  height: 100%;

  > section:last-of-type {
    flex: 1;
  }
`;

const CardTitle = styled.h2`
  margin: 0 0 10px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: white;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.75);
`;

const Input = styled.input`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${props => props.theme.colors.primary};
  padding: 7px 10px;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.25);
    font-weight: 500;
  }
`;

const Select = styled.select`
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${props => props.theme.colors.primary};
  padding: 8px 10px;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 700;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SearchWrap = styled.div`
  position: relative;
`;

const Dropdown = styled.ul`
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 220px;
  overflow-y: auto;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.25);
`;

const DropdownItem = styled.li`
  padding: 8px 10px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &:hover {
    background: rgba(var(--al-primary-rgb), 0.12);
    color: ${props => props.theme.colors.primary};
  }
`;

const LocationRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  color: white;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: ${props => props.theme.colors.primary};
  }
`;

const Hint = styled.p`
  margin: 0 0 8px;
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textDim};
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
`;

const RangeCard = styled.div`
  border: 1px solid rgba(var(--al-primary-rgb), 0.35);
  background: rgba(var(--al-primary-rgb), 0.04);
  padding: 14px;
`;

const RangeHeadline = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const RangeTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: white;

  span {
    display: block;
    margin-top: 3px;
    font-size: 0.72rem;
    font-weight: 500;
    color: ${props => props.theme.colors.textDim};
    text-transform: none;
    letter-spacing: 0;
  }
`;

const RangePrice = styled.div`
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${props => props.theme.colors.primary};
  text-align: right;

  span {
    display: block;
    margin-top: 2px;
    font-size: 0.72rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.65);
  }
`;

const RangeEnds = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const PriceSlider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 8px;
  margin: 16px 0 12px;
  border-radius: 999px;
  background: linear-gradient(90deg, #FFD700 0%, #4CAF50 100%);
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: white;
    border: 3px solid var(--al-primary);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    border: 3px solid var(--al-primary);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  }

  &:focus-visible::-webkit-slider-thumb {
    outline: 2px solid white;
    outline-offset: 2px;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  margin: 14px 0;
`;

const RangeEnd = styled.div`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  color: inherit;
  text-align: inherit;
  cursor: ${props => (props.onClick ? 'pointer' : 'default')};

  .label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${props => props.$color};
    margin-bottom: 2px;
  }

  .price {
    font-size: 1rem;
    font-weight: 700;
    color: white;
  }

  .sub {
    margin-top: 2px;
    font-size: 0.7rem;
    color: ${props => props.theme.colors.textDim};
  }
`;

const Meta = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  font-size: 0.72rem;
  color: ${props => props.theme.colors.textDim};
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;

  strong {
    color: ${props => props.theme.colors.primary};
    font-weight: 700;
  }
`;

const Empty = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.textDim};
  font-size: 0.85rem;
`;

const TotalsRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TotalsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.85rem;

  .label {
    color: rgba(255, 255, 255, 0.75);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.75rem;
  }

  .value {
    color: ${props => props.theme.colors.primary};
    font-weight: 700;
    text-align: right;
  }
`;

const GrandTotalRow = styled(TotalsRow)`
  margin-top: 6px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);

  .label {
    color: white;
    font-size: 0.85rem;
  }

  .value {
    font-size: 1.15rem;
  }
`;

const FeeTaxRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

function SpQuotePage() {
  // Hybrid Stats + production timing; see pricing.js
  const [products, setProducts] = useState([]);
  const [garmentQuery, setGarmentQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('');

  const [quantity, setQuantity] = useState('50');
  const [cogs, setCogs] = useState('');
  const [decorationType, setDecorationType] = useState('screen');
  const [locations, setLocations] = useState({
    front: { enabled: true, colors: '1', stitchTier: 'under5k' },
    back: { enabled: false, colors: '1', stitchTier: 'under5k' },
    other: { enabled: false, colors: '1', stitchTier: 'under5k' },
  });
  // Slider position: 0 = floor, 1 = ask. Kept as a fraction so "at the ask"
  // stays at the ask when qty / garment / decoration change.
  const [pricePosition, setPricePosition] = useState(1);
  const [jobFee, setJobFee] = useState('0');
  const [taxExempt, setTaxExempt] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'products'));
        setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error('SP Quote: failed to load products', err);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (decorationType === 'dtf') {
      setJobFee(String(RETAIL_BASELINES.dtf.setupFee || 20));
      return;
    }
    if (decorationType === 'embroidery') {
      const locCount = ['front', 'back', 'other'].filter((k) => locations[k]?.enabled).length;
      setJobFee(String(embroideryDigitizingFee(locCount, quantity)));
      return;
    }
    setJobFee('0');
  }, [decorationType, locations, quantity]);

  const isDtf = decorationType === 'dtf';
  const isEmbroidery = decorationType === 'embroidery';
  const colorNoun = isEmbroidery ? 'stitch tiers' : 'ink colors';
  const jobFeeLabel = isEmbroidery
    ? 'Digitizing / fees ($)'
    : isDtf
      ? 'Job fee ($)'
      : 'Job fee ($)';

  const quote = calculateQuoteBands({
    quantity,
    cogs,
    decorationType,
    locations,
  });

  const qtyNum = Number(quantity) || 0;
  const greenUnavailable = quote?.greenSource === 'unavailable';
  const askPerPiece = greenUnavailable ? null : quote?.bands?.green?.perPiece ?? null;
  const floorPerPiece = quote?.bands?.yellow?.perPiece ?? null;

  // Slider stops: exact floor and ask at the ends, $0.05 steps between
  const priceStops = buildPriceStops(floorPerPiece, askPerPiece);
  const sliderEnabled = priceStops.length > 1;
  const floorAboveAsk = askPerPiece != null && floorPerPiece != null && floorPerPiece > askPerPiece;
  const sliderIndex = stopIndexForPosition(priceStops, floorPerPiece, askPerPiece ?? floorPerPiece, pricePosition);
  const selectedPerPiece = priceStops.length ? priceStops[sliderIndex] : null;
  const atAsk = selectedPerPiece != null && askPerPiece != null && Math.abs(selectedPerPiece - askPerPiece) < 0.005;
  const atFloor = selectedPerPiece != null && floorPerPiece != null && Math.abs(selectedPerPiece - floorPerPiece) < 0.005;
  const totalsBand = atAsk ? 'green' : atFloor ? 'yellow' : 'custom';

  const handleSliderChange = (index) => {
    setPricePosition(positionForStopIndex(priceStops, floorPerPiece, askPerPiece ?? floorPerPiece, index));
  };

  const jobFeeNum = Math.max(0, parseFloat(jobFee) || 0);
  const merchandiseSubtotal = selectedPerPiece != null ? selectedPerPiece * qtyNum : 0;
  const preTaxTotal = merchandiseSubtotal + jobFeeNum;
  const taxAmount = taxExempt ? 0 : preTaxTotal * DEFAULT_SALES_TAX_RATE;
  const grandTotal = preTaxTotal + taxAmount;
  const effectivePerPiece = qtyNum > 0 ? grandTotal / qtyNum : 0;
  const cogsNum = Number(cogs) || 0;
  const netPerPiece = selectedPerPiece != null
    ? Math.max(0, selectedPerPiece - cogsNum)
    : 0;
  const netIncome = netPerPiece * qtyNum + jobFeeNum;

  const askTotal = greenUnavailable ? null : quote?.bands?.green?.total ?? null;
  const floorTotal = quote?.bands?.yellow?.total ?? null;
  const quoteMinutes = quote?.minutes ?? null;

  const lastLoggedFingerprintRef = useRef('');
  const initialFingerprintRef = useRef(null);

  // After inputs stay unchanged ~30s, log the settled quote for usage tracking.
  // Skips the default first-load state so merely opening the page doesn't count.
  useEffect(() => {
    if (!shouldLogSpQuote({ quantity: qtyNum, quote: { bands: { yellow: { perPiece: floorPerPiece }, green: { perPiece: askPerPiece } } } })) {
      return undefined;
    }

    const fingerprint = buildSpQuoteLogFingerprint({
      quantity: qtyNum,
      cogs: cogsNum,
      decorationType,
      locations,
      selectedStyle,
      garmentQuery,
      totalsBand,
      selectedPerPiece,
      jobFee: jobFeeNum,
      taxExempt,
      askPerPiece,
      floorPerPiece,
      grandTotal,
    });

    if (initialFingerprintRef.current == null) {
      initialFingerprintRef.current = fingerprint;
      return undefined;
    }

    if (
      fingerprint === initialFingerprintRef.current ||
      fingerprint === lastLoggedFingerprintRef.current
    ) {
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        await logSpQuoteUsage({
          quantity: qtyNum,
          cogs: cogsNum,
          decorationType,
          locations,
          selectedStyle,
          garmentQuery,
          totalsBand,
          selectedPerPiece,
          jobFee: jobFeeNum,
          taxExempt,
          minutes: quoteMinutes,
          askPerPiece,
          floorPerPiece,
          askTotal,
          floorTotal,
          merchandiseSubtotal,
          taxAmount,
          grandTotal,
          netIncome,
          greenUnavailable,
          fingerprint,
        });
        lastLoggedFingerprintRef.current = fingerprint;
      } catch (err) {
        console.warn('SP Quote usage log failed', err);
      }
    }, SETTLED_QUOTE_LOG_MS);

    return () => clearTimeout(timer);
  }, [
    qtyNum,
    cogsNum,
    decorationType,
    locations,
    selectedStyle,
    garmentQuery,
    totalsBand,
    selectedPerPiece,
    jobFeeNum,
    taxExempt,
    askPerPiece,
    floorPerPiece,
    askTotal,
    floorTotal,
    quoteMinutes,
    merchandiseSubtotal,
    taxAmount,
    grandTotal,
    netIncome,
    greenUnavailable,
  ]);

  const handleGarmentSearch = (value) => {
    setGarmentQuery(value);
    setSelectedStyle('');
    setShowDropdown(true);

    if (!value.trim()) {
      setFilteredProducts([]);
      return;
    }

    const q = value.toLowerCase();
    const filtered = products.filter(product => {
      const styleMatch = product?.styleNo?.toLowerCase()?.includes(q) || false;
      const descMatch = (product?.description || '').toLowerCase().includes(q);
      const shortDescMatch = (product?.shortDescription || '').toLowerCase().includes(q);
      const brandMatch = (product?.brand || '').toLowerCase().includes(q);
      return styleMatch || descMatch || shortDescMatch || brandMatch;
    }).slice(0, 25);

    setFilteredProducts(filtered);
  };

  const handleSelectProduct = (product) => {
    if (!product) return;
    const label = `${product.styleNo || ''} - ${product.shortDescription || product.description || ''}`.trim();
    setGarmentQuery(label);
    setSelectedStyle(product.styleNo || '');
    setCogs(product.cogs != null && product.cogs !== '' ? String(product.cogs) : '');
    setFilteredProducts([]);
    setShowDropdown(false);
  };

  const handleCogsChange = (value) => {
    const cleaned = value.replace(/[^0-9.]/g, '');
    setCogs(cleaned);
  };

  const toggleLocation = (key) => {
    setLocations(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled },
    }));
  };

  const setLocationColors = (key, colors) => {
    setLocations(prev => ({
      ...prev,
      [key]: { ...prev[key], colors: colors.replace(/[^0-9]/g, '') },
    }));
  };

  const setLocationStitchTier = (key, stitchTier) => {
    setLocations(prev => ({
      ...prev,
      [key]: { ...prev[key], stitchTier },
    }));
  };

  const floorRate = floorRateFor(decorationType);

  return (
    <Page>
      <Layout>
        <Column>
          <Card>
            <CardTitle>Job</CardTitle>
            <Row>
              <Field>
                Quantity (pcs)
                <Input
                  type="text"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="50"
                />
              </Field>
              <Field>
                Decoration
                <Select
                  value={decorationType}
                  onChange={(e) => setDecorationType(e.target.value)}
                >
                  {DECORATION_TYPES.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              </Field>
            </Row>

            <Field>
              Garment
              <SearchWrap>
                <Input
                  type="text"
                  value={garmentQuery}
                  onChange={(e) => handleGarmentSearch(e.target.value)}
                  onFocus={() => garmentQuery.trim() && setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                  placeholder="e.g. Gildan 2000"
                  autoComplete="off"
                />
                {showDropdown && filteredProducts.length > 0 && (
                  <Dropdown>
                    {filteredProducts.map(product => (
                      <DropdownItem
                        key={product.id}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelectProduct(product)}
                      >
                        {product.styleNo}
                        {product.brand ? ` · ${product.brand}` : ''}
                        {' — '}
                        {product.shortDescription || product.description || 'No description'}
                        {product.cogs != null ? ` ($${Number(product.cogs).toFixed(2)})` : ''}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                )}
              </SearchWrap>
            </Field>

            <Field style={{ marginBottom: selectedStyle ? 6 : 0 }}>
              Garment cost ($/pc)
              <Input
                type="text"
                inputMode="decimal"
                value={cogs}
                onChange={(e) => handleCogsChange(e.target.value)}
                placeholder="3.38"
              />
            </Field>
            {selectedStyle && (
              <Hint style={{ marginBottom: 0 }}>Using catalog style {selectedStyle}. Edit cost above to override.</Hint>
            )}
          </Card>

          <Card>
            <CardTitle>Locations &amp; {isDtf ? 'placements' : colorNoun}</CardTitle>
            {isDtf ? (
              <Hint>DTF timing is 2 min per location per piece (3 locations = 6 min/garment).</Hint>
            ) : isEmbroidery ? (
              <Hint>
                Website embroidery is decoration-only (garment COGS added). Digitizing $45/location
                {Number(quantity) > 0 && Number(quantity) < 8 ? ' · +$45 under 8 pcs' : ''}.
              </Hint>
            ) : (
              <Hint>Enable each location and enter {colorNoun}. Time = max(Stats, print-list).</Hint>
            )}
            {LOCATIONS.map(loc => {
              const entry = locations[loc.key];
              return (
                <LocationRow key={loc.key}>
                  <CheckLabel>
                    <input
                      type="checkbox"
                      checked={!!entry.enabled}
                      onChange={() => toggleLocation(loc.key)}
                    />
                    {loc.label}
                  </CheckLabel>
                  {isEmbroidery ? (
                    <Select
                      disabled={!entry.enabled}
                      value={entry.stitchTier || 'under5k'}
                      onChange={(e) => setLocationStitchTier(loc.key, e.target.value)}
                      style={{ opacity: entry.enabled ? 1 : 0.35 }}
                    >
                      {EMBROIDERY_STITCH_TIERS.map((tier) => (
                        <option key={tier.value} value={tier.value}>{tier.label}</option>
                      ))}
                    </Select>
                  ) : !isDtf ? (
                    <Input
                      type="text"
                      inputMode="numeric"
                      disabled={!entry.enabled}
                      value={entry.colors}
                      onChange={(e) => setLocationColors(loc.key, e.target.value)}
                      placeholder="# colors"
                      style={{ opacity: entry.enabled ? 1 : 0.35 }}
                    />
                  ) : null}
                </LocationRow>
              );
            })}
          </Card>
        </Column>

        <Column>
          <Card>
            <CardTitle>Quote</CardTitle>
            {!quote || selectedPerPiece == null ? (
              <Empty>Enter quantity and garment cost to see the negotiation range.</Empty>
            ) : (
              <>
                <RangeCard>
                  <RangeHeadline>
                    <RangeTitle>
                      Price per piece
                      <span>
                        {greenUnavailable
                          ? `Floor ($${floorRate}/hr) · no website price for this job`
                          : quote.retail?.decorationOnly
                            ? `Floor ($${floorRate}/hr) → retail decoration + garment · ${quote.retail.source}`
                            : `Floor ($${floorRate}/hr) → retail print + garment${quote.retail?.source ? ` · ${quote.retail.source}` : ''}`}
                      </span>
                    </RangeTitle>
                    <RangePrice>
                      {money(selectedPerPiece)}
                      <span>
                        {atAsk ? 'At ask' : atFloor ? 'At floor' : `${money(selectedPerPiece - floorPerPiece)} over floor`}
                        {' · '}{qtyNum} pcs
                      </span>
                    </RangePrice>
                  </RangeHeadline>

                  {sliderEnabled && (
                    <PriceSlider
                      type="range"
                      min={0}
                      max={priceStops.length - 1}
                      step={1}
                      value={sliderIndex}
                      onChange={(e) => handleSliderChange(Number(e.target.value))}
                      // Floor stays on the left even when it's above the ask
                      style={{ direction: floorAboveAsk ? 'rtl' : 'ltr' }}
                      aria-label="Price per piece"
                      aria-valuetext={money(selectedPerPiece)}
                      title={`$${PRICE_STEP.toFixed(2)} steps · arrow keys for fine control`}
                    />
                  )}

                  <RangeEnds style={sliderEnabled ? undefined : { marginTop: 12 }}>
                    <RangeEnd
                      as="button"
                      type="button"
                      $color="#FFD700"
                      onClick={() => setPricePosition(0)}
                      title="Set price to the floor"
                    >
                      <div className="label">Floor · ${floorRate}/hr</div>
                      <div className="price">{money(floorPerPiece)}/pc</div>
                      <div className="sub">
                        net {money(Math.max(0, floorPerPiece - cogsNum) * qtyNum)}
                      </div>
                    </RangeEnd>
                    {greenUnavailable ? (
                      <RangeEnd $color="#4CAF50" style={{ textAlign: 'right' }}>
                        <div className="label">Retail ask</div>
                        <div className="price">—</div>
                        <div className="sub">{quote.greenUnavailableReason || 'Unavailable'}</div>
                      </RangeEnd>
                    ) : (
                      <RangeEnd
                        as="button"
                        type="button"
                        $color="#4CAF50"
                        style={{ textAlign: 'right' }}
                        onClick={() => setPricePosition(1)}
                        title="Set price to the ask"
                      >
                        <div className="label">Ask · website retail</div>
                        <div className="price">{money(askPerPiece)}/pc</div>
                        <div className="sub">
                          net {money(Math.max(0, askPerPiece - cogsNum) * qtyNum)}
                        </div>
                      </RangeEnd>
                    )}
                  </RangeEnds>
                </RangeCard>

                <Hint style={{ marginTop: 10, marginBottom: 0 }}>
                  {floorAboveAsk ? (
                    <>
                      <strong style={{ color: '#FFD700' }}>Website price is below the floor</strong>
                      {' '}by {money(floorPerPiece - askPerPiece)}/pc on this job.
                    </>
                  ) : greenUnavailable ? (
                    <>Pricing at the floor — no website price for this job.</>
                  ) : (
                    <>
                      Room to move:{' '}
                      <strong style={{ color: 'var(--al-primary)' }}>
                        {money(Math.max(0, askPerPiece - floorPerPiece))}
                      </strong>
                      /pc
                    </>
                  )}
                  {quote.retail?.baselineCogs != null && Number(cogs) !== quote.retail.baselineCogs ? (
                    <> (website {money(quote.retail.baselinePerPiece)} includes {quote.retail.source} @ {money(quote.retail.baselineCogs)}; ask = {money(quote.retail.decorationPerPiece)} print + your {money(Number(cogs))} garment)</>
                  ) : null}
                </Hint>
                <Meta>
                  <span>Est. print time <strong>{quote.timeLabel}</strong></span>
                  {quote.method === 'screen' && (
                    <span>
                      Stats <strong>{formatMinutes(quote.statsMinutes)}</strong>
                      {' / '}
                      production <strong>{formatMinutes(quote.productionMinutes)}</strong>
                    </span>
                  )}
                  <span>Garment <strong>{money(Number(cogs))}</strong>/pc</span>
                  <span>Decoration <strong>{DECORATION_TYPES.find(d => d.value === decorationType)?.label}</strong></span>
                </Meta>

                <Divider />

                <FeeTaxRow>
                  <Field style={{ marginBottom: 0 }}>
                    {jobFeeLabel}
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={jobFee}
                      onChange={(e) => setJobFee(e.target.value.replace(/[^0-9.]/g, ''))}
                      placeholder={isEmbroidery ? '45' : isDtf ? '20' : '0'}
                    />
                  </Field>
                  <Field style={{ marginBottom: 0 }}>
                    Sales tax
                    <CheckLabel style={{ marginTop: 8, textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                      <input
                        type="checkbox"
                        checked={taxExempt}
                        onChange={(e) => setTaxExempt(e.target.checked)}
                      />
                      Tax exempt
                    </CheckLabel>
                    {!taxExempt && (
                      <Hint style={{ marginTop: 6, marginBottom: 0 }}>
                        {(DEFAULT_SALES_TAX_RATE * 100).toFixed(0)}% on merchandise + job fee
                      </Hint>
                    )}
                  </Field>
                </FeeTaxRow>

                <TotalsRows>
                  <TotalsRow>
                    <span className="label">Per piece</span>
                    <span className="value">{money(selectedPerPiece)}</span>
                  </TotalsRow>
                  <TotalsRow>
                    <span className="label">Merchandise ({qtyNum} pcs)</span>
                    <span className="value">{money(merchandiseSubtotal)}</span>
                  </TotalsRow>
                  <TotalsRow>
                    <span className="label">Job fee</span>
                    <span className="value">{money(jobFeeNum)}</span>
                  </TotalsRow>
                  <TotalsRow>
                    <span className="label">
                      Tax{taxExempt ? ' (exempt)' : ` (${(DEFAULT_SALES_TAX_RATE * 100).toFixed(0)}%)`}
                    </span>
                    <span className="value">{money(taxAmount)}</span>
                  </TotalsRow>
                  <GrandTotalRow>
                    <span className="label">Grand total</span>
                    <span className="value">{money(grandTotal)}</span>
                  </GrandTotalRow>
                  <TotalsRow>
                    <span className="label">Effective / pc</span>
                    <span className="value">{money(effectivePerPiece)}</span>
                  </TotalsRow>
                  <GrandTotalRow>
                    <span className="label">Net income</span>
                    <span className="value">{money(netIncome)}</span>
                  </GrandTotalRow>
                  <TotalsRow>
                    <span className="label">Net / pc (ex. COGS)</span>
                    <span className="value">{money(netPerPiece)}</span>
                  </TotalsRow>
                  <Hint style={{ marginTop: 8, marginBottom: 0 }}>
                    Net = (price − garment COGS) × qty{jobFeeNum > 0 ? ' + job fee' : ''}. Tax not included.
                  </Hint>
                </TotalsRows>
              </>
            )}
          </Card>
        </Column>
      </Layout>
    </Page>
  );
}

export default SpQuotePage;
